import { css } from "@emotion/react";
import React, { useCallback, useEffect, useRef } from "react";
import { AsComponentProp } from "../AsComponentProps";
import { useTheme } from "../theme";
import { useTab } from "./useTab";

interface BaseTabProps {
  index?: number;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

export type TabProps<E extends React.ElementType> = AsComponentProp<
  E,
  BaseTabProps
>;

/**
 * @deprecated Use TabUI instead
 */
export const Tab = <E extends React.ElementType = "button">({
  children,
  index = 0,
  fullWidth,
  as,
  ...rest
}: TabProps<E>) => {
  const theme = useTheme();
  const { currentIndex, setCurrentIndex, setCurrentTab } = useTab();
  const handleClick = useCallback(() => {
    setCurrentIndex(index);
  }, [index, setCurrentIndex]);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (currentIndex === index && ref.current) {
      setCurrentTab(ref.current as unknown as HTMLDivElement);
    }
  }, [currentIndex, index, setCurrentTab]);
  const Component: React.ElementType = as ?? "button";
  return (
    <Component
      ref={ref}
      onClick={handleClick}
      {...rest}
      css={[
        {
          position: "relative",
          boxSizing: "border-box",
          color: "inherit",
          border: 0,
          margin: 0,
          display: "inline-flex",
          outline: 0,
          padding: 0,
          alignItems: "center",
          userSelect: "none",
          borderRadius: 0,
          verticalAlign: "middle",
          justifyContent: "center",
          textDecoration: "none",
          backgroundColor: "transparent",
          appearance: "none",
          WebkitTapHighlightColor: "transparent",
          textRendering: "auto",
          wordSpacing: "nomral",
          textIndent: "0px",
          textShadow: "none",
          ...theme.typography.body,
          flexShrink: 0,
          cursor: "pointer",
          transition: "0.45s",
          minHeight: theme.spacing * 5,
          paddingLeft: theme.spacing * 2,
          paddingRight: theme.spacing * 2,
        },
        fullWidth
          ? css`
              flex-basis: 0;
              flex-grow: 1;
              flex-shrink: 1;
              max-width: none;
            `
          : null,
      ]}
    >
      {children}
    </Component>
  );
};
